// ============================================================================
// MarketNotifySettingsCard — уведомления маркетплейса и чатов.
// Ключи — существующие user_settings (settingsStore): их же читают живая
// лента мастера (notifications.market_requests*), чат (chat.notify) и звуки
// (sound.*). Изменения сохраняются сразу, отдельных кнопок нет.
// ============================================================================

import { useEffect, useState } from 'react';
import { Bell, Volume2 } from 'lucide-react';
import {
  loadMarketNotifySettings,
  setMarketNotifySetting,
  subscribeMarketNotifySettings,
  type MarketNotifyKey,
  type MarketNotifySettings,
} from '@/lib/marketProfile';
import { settingsStore } from '@/lib/settingsStore';

function ToggleRow({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className={`flex items-start justify-between gap-3 py-2 ${disabled ? 'opacity-60' : ''}`}>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-medium">{label}</span>
        <span className="mt-0.5 block text-[10px] leading-4 text-muted-foreground">{hint}</span>
      </span>
      <input
        type="checkbox"
        className="mt-0.5 h-5 w-5 shrink-0"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}

export function MarketNotifySettingsCard() {
  const [settings, setSettings] = useState<MarketNotifySettings>(() => loadMarketNotifySettings());

  useEffect(() => {
    void settingsStore.hydrate();
    return subscribeMarketNotifySettings(() => setSettings(loadMarketNotifySettings()));
  }, []);

  const set = (key: MarketNotifyKey, value: boolean) => {
    setMarketNotifySetting(key, value);
    setSettings(loadMarketNotifySettings());
  };

  return (
    <section className="space-y-2 rounded-2xl border bg-card p-4">
      <div className="flex items-center gap-2">
        <Bell className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-semibold">Уведомления и чаты</h2>
      </div>
      <p className="text-[11px] leading-5 text-muted-foreground">
        Настройки применяются сразу и сохраняются на сервере.
      </p>

      <div className="divide-y">
        <ToggleRow
          label="Уведомления маркетплейса"
          hint="Новые заявки и события маркетплейса (push и уведомления платформы)."
          checked={settings.market}
          onChange={(v) => set('market', v)}
        />
        <ToggleRow
          label="Показывать новые заявки в приложении"
          hint="Подсветка и бейдж в живой ленте. Без основного переключателя не показывается."
          checked={settings.marketInApp}
          disabled={!settings.market}
          onChange={(v) => set('marketInApp', v)}
        />
        <ToggleRow
          label="Сообщения в чате"
          hint="Всплывающие уведомления о сообщениях, когда чат закрыт."
          checked={settings.chat}
          onChange={(v) => set('chat', v)}
        />
      </div>

      <div className="flex items-center gap-2 border-t pt-3">
        <Volume2 className="h-4 w-4 text-primary" />
        <h3 className="text-xs font-semibold">Звуки</h3>
      </div>
      <div className="divide-y">
        <ToggleRow
          label="Звуковые оповещения"
          hint="Общий выключатель звуков платформы."
          checked={settings.soundEnabled}
          onChange={(v) => set('soundEnabled', v)}
        />
        <ToggleRow
          label="Звук новых заявок"
          hint="Звучит при появлении заявки в ленте (если включены уведомления маркетплейса)."
          checked={settings.soundNotify}
          disabled={!settings.soundEnabled}
          onChange={(v) => set('soundNotify', v)}
        />
        <ToggleRow
          label="Звук сообщений"
          hint="Звучит при новом сообщении в чате."
          checked={settings.soundChat}
          disabled={!settings.soundEnabled}
          onChange={(v) => set('soundChat', v)}
        />
      </div>
    </section>
  );
}
